<!-- ============================================================
  EventManager.vue — หน้า "อีเวนต์ / แข่งขัน" (v3 SPLIT)

  เจ้าของสั่ง 2026-08-12 (EVENT_SYSTEM_DESIGN.md §8 กล่อง 🔄):
    • อีเวนต์แยกตารางของตัวเอง (tbooking_event) — จัดการที่หน้านี้ที่เดียว
      **ห้ามปนกับหน้าจองคลาสกลุ่ม** (session "แข่ง" ในหน้านั้นคือ workaround ยุคเก่า)
    • รายการแสดง **ทั้งหมด** ไม่มีกรอบ 90 วัน + filter ปี/โปรแกรม/ค้นหา
    • สวิตช์ "ราคา" กับ "การรับสมัคร" แยกกัน (ดู EventConfigForm.vue)
    • อีเวนต์รวมทุกโปรแกรมได้ (courseid = null) — เด็กคอร์สไหนก็เข้าร่วมได้
    • อีเวนต์รุ่นเก่า (ยังอยู่บน tclassinfo/tbooking_group) แสดงคู่กันแบบ **อ่าน/เช็คชื่อ** เท่านั้น
      ติดธง legacy — แก้ไข/เพิ่มชื่อ/ลบ ทำไม่ได้ (รอเฟสย้ายข้อมูลที่จะวางแผนภายหลัง)

  โหมดผู้เข้าร่วม 2 แบบ (เลิกโหมด 'move' ของ v1 — อีเวนต์ไม่ยุ่งกับใบจองคลาสกลุ่มแล้ว):
    'deduct' หักโควตาคอร์สของเด็ก 1 ครั้ง (บันทึกคู่ใน tevent_quota_use ฝั่ง backend)
    'free'   ลงชื่อไม่หักโควตา
============================================================ -->
<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-trophy-outline"></span></div>
      <div>
        <div class="id-h1">{{ $t('eventMgr.tab') }}</div>
        <div class="pg-sub">{{ $t('eventMgr.pageSubtitle') }}</div>
      </div>
    </div>

    <!-- ===== รายการอีเวนต์ (ทั้งหมด + filter) ===== -->
    <div class="scard" style="padding:16px">
      <id-data-grid :columns="eventColumns" :rows="eventRows" :search-keys="['eventname', 'programLabel']"
        :search-placeholder="$t('btn.search')" :loading="loadingEvents" :filters="gridFilters">
        <template #actions>
          <button class="id-btn id-btn-ghost id-btn-sm" @click="loadEvents">
            <span class="mdi mdi-refresh"></span> {{ $t('btn.refresh') }}
          </button>
          <button class="id-btn id-btn-primary id-btn-sm" @click="openEventDialog(null)">
            <span class="mdi mdi-trophy-outline"></span> {{ $t('eventMgr.newEvent') }}
          </button>
        </template>
        <!-- อีเวนต์รุ่นเก่าแบบหลายวัน (แถว "แข่ง" ยุค hack) แสดงเป็นช่วงวันที่มีคนเข้าร่วมจริง
             ไม่ใช่ startdate ของคลาสซึ่งเป็น 1 ม.ค. 2566 ทุกแถว (เจ้าของทัก 2026-08-12) -->
        <template #cell-eventdate="{ row }">
          <template v-if="Number(row.daycount) > 1">
            {{ formatDate(row.eventdate) }} – {{ formatDate(row.lastdate) }}
            <span class="ev-dim">({{ $t('evV3.dayCount', { n: row.daycount }) }})</span>
          </template>
          <template v-else>{{ formatDate(row.eventdate) }}</template>
        </template>
        <template #cell-eventname="{ row }">
          {{ row.eventname }}
          <span v-if="row.legacy" class="ev-tag ev-tag-warn" v-hint="$t('evV3.legacyHint')">{{ $t('evV3.legacyBadge') }}</span>
          <span v-if="!row.legacy && Number(row.pricing_enabled) === 1" class="ev-tag ev-tag-info">{{ $t('evV3.hasPrice') }}</span>
          <span v-if="!row.legacy && Number(row.registration_enabled) === 1" class="ev-tag ev-tag-info">{{ $t('evV3.regOpen') }}</span>
        </template>
        <template #cell-kids="{ row }">{{ row.kids }} <span class="ev-dim">({{ $t('eventMgr.checkedinShort') }} {{ row.checkedin }})</span></template>
        <template #cell-unpaid="{ row }">
          <span v-if="row.legacy" class="ev-dim">—</span>
          <span v-else-if="Number(row.unpaid) > 0" class="ev-tag ev-tag-warn">{{ row.unpaid }}</span>
          <span v-else class="ev-dim">—</span>
        </template>
        <template #cell-total_amount="{ row }">
          <span v-if="row.legacy" class="ev-dim">—</span>
          <span v-else>{{ $money(row.total_amount) }}</span>
        </template>
        <template #cell-roster="{ row }">
          <span class="mdi mdi-account-group-outline" style="color:var(--c-primary);cursor:pointer;font-size:20px" @click="openRoster(row)"></span>
        </template>
        <!-- รายงานเลื่อนเลเวลต่ออีเวนต์ (เจ้าของสั่ง 2026-10-26) — กดแล้วเปิดแท็บผลการแข่งของงานนั้น -->
        <template #cell-levelchg="{ row }">
          <span v-if="row.legacy || !(Number(row.level_up) || Number(row.level_down))" class="ev-dim">—</span>
          <button v-else type="button" class="ev-lvchg" v-hint="$t('levelRes.colHint')" :aria-label="$t('levelRes.colHint')"
            @click="openResults(row)">
            <span class="ev-lvup"><span class="mdi mdi-arrow-up-bold"></span>{{ row.level_up }}</span>
            <span v-if="Number(row.level_down)" class="ev-lvdown"><span class="mdi mdi-arrow-down-bold"></span>{{ row.level_down }}</span>
          </button>
        </template>
        <template #cell-remit="{ row }">
          <span v-if="row.legacy" class="ev-dim">—</span>
          <span v-else class="mdi mdi-bank-transfer-out" style="color:var(--c-primary);cursor:pointer;font-size:20px"
            v-hint="$t('evRemit.title')" :aria-label="$t('evRemit.title')" @click="openRemit(row)"></span>
        </template>
        <template #cell-edit="{ row }">
          <span v-if="row.legacy" class="mdi mdi-lock-outline" style="color:var(--c-border-strong);font-size:20px" v-hint="$t('evV3.legacyHint')"></span>
          <span v-else class="mdi mdi-pencil" style="color:var(--c-info);cursor:pointer;font-size:20px" @click="openEventDialog(row)"></span>
        </template>
        <template #cell-delete="{ row }">
          <span v-if="row.legacy" class="mdi mdi-lock-outline" style="color:var(--c-border-strong);font-size:20px" v-hint="$t('evV3.legacyHint')"></span>
          <span v-else class="mdi mdi-delete-forever"
            :style="{ color: Number(row.kids) === 0 ? 'var(--c-error)' : 'var(--c-border-strong)', cursor: Number(row.kids) === 0 ? 'pointer' : 'not-allowed', fontSize: '20px' }"
            v-hint="Number(row.kids) === 0 ? '' : $t('eventMgr.deleteBlockedHint')" @click="Number(row.kids) === 0 && askDeleteEvent(row)"></span>
        </template>
      </id-data-grid>
    </div>

    <!-- ===== สร้าง / แก้ไขอีเวนต์ ===== -->
    <id-modal v-model="eventDialog" size="lg" icon="mdi-trophy-outline"
      :title="editingId ? $t('evV3.editEvent') : $t('eventMgr.newEvent')" persistent>
      <div class="form-grid">
        <div class="field">
          <label>{{ $t('eventMgr.date') }} <span class="req">*</span></label>
          <!-- non-head เลือกวันย้อนหลังไม่ได้ (backend ปฏิเสธอยู่แล้ว) — ปิดที่ปฏิทินจะได้ไม่เสียเวลากรอก -->
          <id-date v-model="form.eventdate" :placeholder="$t('eventMgr.datePh')" :mindate="isHeadUser ? null : todayISO"></id-date>
          <div v-if="!isHeadUser" class="ev-dim" style="margin-top:4px">{{ $t('eventMgr.pastHeadOnly') }}</div>
        </div>
        <div class="field">
          <label>{{ $t('evV3.starttime') }}</label>
          <input class="id-input" v-model="form.starttime" maxlength="20" :placeholder="$t('evV3.starttimePh')">
        </div>
        <div class="field full">
          <label>{{ $t('eventMgr.label') }} <span class="req">*</span></label>
          <input class="id-input" v-model="form.eventname" maxlength="90" :placeholder="$t('eventMgr.labelPh')">
          <div class="fhint"><span class="mdi mdi-lightbulb-on-outline"></span> {{ $t('eventMgr.labelHint') }}</div>
        </div>
        <div class="field">
          <label>{{ $t('evV3.program') }}</label>
          <!-- ค่าว่าง = อีเวนต์รวมทุกโปรแกรม (เด็กคอร์สไหนก็เข้าร่วมได้) -->
          <id-select v-model="form.courseid" :options="programOptions" :placeholder="$t('evV3.allPrograms')"></id-select>
          <div class="fhint">{{ $t('evV3.programHint') }}</div>
        </div>
        <div class="field">
          <label>{{ $t('evV3.maxperson') }}</label>
          <input class="id-input" type="number" min="1" max="999" v-model="form.maxperson">
        </div>
        <div class="field full">
          <label>{{ $t('evV3.note') }}</label>
          <input class="id-input" v-model="form.note" maxlength="500">
        </div>
      </div>
      <event-config-form :cfg="cfgForm"></event-config-form>
      <template #footer>
        <!-- ยกเลิกทั้งงาน: head เท่านั้น (backend ตอบ 403 ให้แอดมิน) และเฉพาะอีเวนต์ที่มีอยู่แล้ว
             วางไว้ซ้ายสุดแยกจากปุ่มบันทึก — เป็นการกระทำคนละชนิดกัน ไม่ควรอยู่ติดกัน -->
        <button v-if="editingId && isHeadUser" class="id-btn id-btn-ghost ev-danger-btn"
          :disabled="saving" style="margin-right:auto" @click="askCancelWholeEvent()">
          <span class="mdi mdi-calendar-remove-outline"></span> {{ $t('evV5.cancelWhole') }}
        </button>
        <button class="id-btn id-btn-ghost" :disabled="saving" @click="eventDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="saving || !canSaveEvent" @click="saveEvent">
          <span class="mdi mdi-content-save"></span> {{ editingId ? $t('btn.ok') : $t('eventMgr.create') }}
        </button>
      </template>
    </id-modal>

    <!-- ===== รายชื่อผู้เข้าร่วม ===== -->
    <!-- steady-height: แท็บ "มาเรียน" กับ "ยกเลิก" จำนวนแถวไม่เท่ากัน กล่องจะได้ไม่กระตุกตอนสลับ -->
    <id-modal v-model="rosterOpen" size="lg" steady-height icon="mdi-account-group-outline"
      :title="rosterEvent ? rosterEvent.eventname + ' · ' + formatDate(rosterEvent.legacydate || rosterEvent.eventdate) : ''">
      <div v-if="rosterLegacy" class="ev-legacy-note">
        <span class="mdi mdi-information-outline"></span> {{ $t('evV3.legacyRosterHint') }}
      </div>
      <!-- แท็บโผล่เฉพาะเมื่อมีประวัติการยกเลิกจริง — อีเวนต์ที่ไม่มีใครยกเลิกไม่ต้องมีแท็บให้กด
           (เจ้าของสั่ง 13 ส.ค.: ยกเลิกที่เป็นธุรกรรมจริงต้องตามดูได้ · 'กรอกผิด' ไม่อยู่ในนี้) -->
      <!-- แท็บ "ผลการแข่ง / เลื่อนเลเวล" มีทุกอีเวนต์ v3 (ก่อนวันแข่ง แพนบอกว่ายังเปิดไม่ได้) — LEVEL_PROMOTION_DESIGN.md -->
      <div v-if="!rosterLegacy" class="ev-tabs">
        <button class="ev-tab" :class="{ on: rosterTab === 'active' }" @click="rosterTab = 'active'">
          <span class="mdi mdi-account-group-outline"></span> {{ $t('evV6.tabActive', { n: roster.length }) }}
        </button>
        <button class="ev-tab" :class="{ on: rosterTab === 'results' }" @click="rosterTab = 'results'">
          <span class="mdi mdi-podium"></span> {{ $t('levelRes.tab') }}
        </button>
        <button v-if="cancelledList.length > 0" class="ev-tab" :class="{ on: rosterTab === 'cancelled' }" @click="rosterTab = 'cancelled'">
          <span class="mdi mdi-history"></span> {{ $t('evV6.tabCancelled', { n: cancelledList.length }) }}
        </button>
      </div>
      <!-- 2 แพนนี้ใช้ v-show + .id-tabpane ตามมาตรฐาน steady-height ของ IdModal — ต้องอยู่ใน DOM
           ทั้งคู่ dialog ถึงจะวัดความสูงล่วงหน้าแล้วล็อกไว้ตั้งแต่เปิดได้ (ห้ามเปลี่ยนกลับเป็น v-if) -->
      <div v-if="!rosterLegacy" v-show="rosterTab === 'results'" class="id-tabpane">
        <event-level-results :data="levelResults" :eventid="rosterEvent && rosterEvent.eventid"
          @reload="openRoster(rosterEvent, true); loadEvents()" @onLoading="$emit('onLoading', $event)"
          @onErrorHandler="$emit('onErrorHandler', $event)" @onInfoHandler="$emit('onInfoHandler', $event)"></event-level-results>
      </div>
      <div v-show="rosterTab === 'cancelled'" class="id-tabpane">
        <div v-for="c in cancelledList" :key="'c' + c.registrationid" class="ev-rosterrow">
          <span class="ev-rostername">
            {{ c.nickname }} <span class="ev-dim">{{ c.fullname }}</span>
            <span class="ev-tag" :class="cancelTagClass(c)">{{ cancelKindLabel(c) }}</span>
            <span class="ev-tag">{{ $baht(Number(c.gym_fee) + Number(c.organizer_fee)) }}</span>
            <span v-if="c.refund_amount !== null && c.refund_amount !== undefined" class="ev-tag ev-tag-warn">
              {{ $t('evV6.refunded', { amount: $baht(c.refund_amount), date: formatDate(c.refund_date) }) }}
            </span>
            <span v-if="c.cancel_kind === 'forfeit' && Number(c.organizer_fee) > 0" class="ev-tag ev-tag-info">
              {{ Number(c.organizer_kept) === 1 ? $t('evV6.orgKeep') : $t('evV6.orgRemit') }}
            </span>
            <span class="ev-dim" style="display:block;margin-top:2px;font-size:12px">
              {{ $t('evV6.cancelledMeta', { date: formatDate(c.cancelledate), by: c.cancelby || '-' }) }}
              <template v-if="c.refundby"> · {{ $t('evV6.recordedBy', { by: c.refundby }) }}</template>
            </span>
          </span>
        </div>
      </div>
      <div v-show="rosterTab === 'active'" class="id-tabpane">
      <div v-for="r in roster" :key="r.registrationid || r.reservationid" class="ev-rosterrow">
        <span class="ev-rostername">
          {{ r.nickname }} <span class="ev-dim">{{ r.fullname }}</span>
          <span v-if="rosterLegacy">
            <!-- ใบรุ่นเก่าใบเดียวครอบหลายวัน — ต้องบอกว่าแถวนี้ของวันไหน ไม่งั้นอ่านไม่รู้เรื่อง -->
            <span class="ev-tag">{{ formatDate(r.classdate) }}</span>
            <span v-if="Number(r.freeflag) === 1" class="ev-tag ev-tag-info">{{ $t('eventMgr.tagListed') }}</span>
          </span>
          <template v-else>
            <span v-if="r.quota_mode === 'deduct'" class="ev-tag" v-hint="$t('evV3.deductHint')">{{ $t('evV3.deducted') }}</span>
            <span v-else class="ev-tag ev-tag-info">{{ $t('eventMgr.tagListed') }}</span>
            <span v-if="hasMoney(r)" class="ev-tag" :class="Number(r.paid) === 1 ? 'ev-tag-paid' : 'ev-tag-warn'">
              {{ $baht(Number(r.gym_fee) + Number(r.organizer_fee)) }} ·
              {{ Number(r.paid) === 1 ? $t('evReg.paid') : $t('evReg.unpaid') }}
            </span>
            <span v-if="hasMoney(r)" class="ev-dim" style="font-size:12px">
              <template v-if="r.level">{{ $t('evReg.lvShort') }} {{ r.level }}<template v-if="r.current_level && !lvSame(r.current_level, r.level)"> ({{ $t('evReg.nowLevel', { n: r.current_level }) }})</template> · </template>{{ $t('evReg.gymPart') }} {{ $baht(r.gym_fee) }} · {{ $t('evReg.orgPart') }} {{ $baht(r.organizer_fee) }}
            </span>
            <span v-if="Number(r.price_overridden) === 1" class="ev-tag ev-tag-info" v-hint="$t('evReg.overridden')">✎</span>
            <a v-if="r.slip_image_url" :href="r.slip_image_url" target="_blank" class="mdi mdi-receipt-text-outline"
              style="color:var(--c-info)" v-hint="$t('customerCourse.slipTitle')"></a>
          </template>
        </span>
        <span class="ev-rosteract">
          <span v-if="Number(r.checkedin) === 0" class="mdi mdi-check-bold" style="color:var(--c-success);cursor:pointer" v-hint="$t('table.checkin')" @click="checkin(r, 1)"></span>
          <span v-else class="mdi mdi-close-thick" style="color:var(--c-error);cursor:pointer" v-hint="$t('eventMgr.undoCheckin')" @click="checkin(r, 0)"></span>
          <span v-if="Number(r.remit_locked) === 1" class="mdi mdi-lock-outline" style="color:var(--c-warning)" v-hint="$t('evReg.remitLocked')" :aria-label="$t('evReg.remitLocked')"></span>
          <template v-else-if="!rosterLegacy">
            <span class="mdi mdi-cash-edit" style="color:var(--c-info);cursor:pointer" v-hint="$t('evReg.editMoney')" @click="openRegEdit(r)"></span>
            <label class="mdi mdi-paperclip" style="color:var(--c-info);cursor:pointer" v-hint="$t('evReg.attachSlip')">
              <input type="file" accept="image/*" style="display:none" @change="uploadRegSlip($event, r)">
            </label>
          </template>
          <span v-if="Number(r.remit_locked) !== 1" class="mdi mdi-account-remove-outline" style="color:var(--c-error);cursor:pointer" v-hint="$t('eventMgr.removeKid')" @click="askRemove(r)"></span>
        </span>
      </div>
      <div v-if="roster.length === 0" class="ev-dim">{{ $t('eventMgr.noKidsYet') }}</div>
      </div>
      <template #footer>
        <span class="foot-left">
          <button v-if="!rosterLegacy && (isHeadUser || !rosterIsPast)" class="id-btn id-btn-primary id-btn-sm" @click="openAddDialog()">
            <span class="mdi mdi-account-plus-outline"></span> {{ $t('evV3.addParticipants') }}
          </button>
          <span v-else-if="!rosterLegacy" class="ev-dim">{{ $t('eventMgr.pastHeadOnly') }}</span>
        </span>
        <button class="id-btn id-btn-ghost" @click="rosterOpen = false">{{ $t('btn.close') }}</button>
      </template>
    </id-modal>

    <!-- ===== เพิ่มผู้เข้าร่วม (เลือกเด็ก → ตรวจสอบ/เลือกโหมด → บันทึก) ===== -->
    <id-modal v-model="addOpen" size="lg" icon="mdi-account-plus-outline" :title="$t('evV3.addParticipants')" persistent>
      <div class="field full">
        <label>{{ $t('eventMgr.pickKids') }}</label>
        <input class="id-input" v-model="kidSearch" :placeholder="$t('eventMgr.searchKidPh')">
      </div>
      <div class="ev-chiprow">
        <span v-for="s in selectedKids" :key="s.studentid" class="ev-chip">
          {{ s.name }} <span class="mdi mdi-close" @click="removeKid(s.studentid)"></span>
        </span>
        <span v-if="selectedKids.length === 0" class="ev-dim">{{ $t('eventMgr.noKidsYet') }}</span>
      </div>
      <div v-if="review.length === 0" class="ev-picklist">
        <div v-for="s in filteredLookup" :key="s.studentid" class="ev-pickrow" @click="addKid(s)">
          <span class="mdi mdi-account-plus-outline"></span> {{ s.name }}
        </div>
        <div v-if="filteredLookup.length === 0" class="ev-dim" style="padding:10px">{{ $t('eventMgr.noMatch') }}</div>
      </div>

      <!-- ผลตรวจต่อคน: สถานะแพ็กเกจ + ราคาที่ระบบคิด + โหมด -->
      <div v-for="k in review" :key="k.studentid" class="ev-review">
        <div class="ev-review-head">
          <b>{{ k.nickname }}</b>
          <span v-if="k.isTrial" class="ev-tag ev-tag-info">{{ $t('eventMgr.tagTrial') }}</span>
          <span v-else-if="k.noCourse" class="ev-tag ev-tag-warn">{{ $t('eventMgr.tagNoCourse') }}</span>
          <span v-else-if="k.expired" class="ev-tag ev-tag-warn">{{ $t('eventMgr.tagExpired') }}</span>
          <span v-else-if="k.quotaEmpty" class="ev-tag ev-tag-warn">{{ $t('eventMgr.tagQuotaEmpty') }}</span>
          <span v-else-if="k.coursetype === 'Monthly'" class="ev-tag ev-tag-info">{{ $t('eventMgr.tagMonthly') }}</span>
          <span v-else class="ev-tag">{{ $t('eventMgr.tagRemaining', { n: k.remaining }) }}</span>
          <span v-if="k.willStartClock" class="ev-tag ev-tag-warn">{{ $t('eventMgr.tagClockStart') }}</span>
          <span v-if="k.alreadyInEvent" class="ev-tag ev-tag-warn">{{ $t('eventMgr.tagAlreadyIn') }}</span>
          <span v-if="k.programBlocked" class="ev-tag ev-tag-warn">{{ $t('evV3.programBlocked') }}</span>
          <span v-if="k.price" class="ev-tag ev-tag-paid">
            {{ $baht(Number(k.price.gym_fee) + Number(k.price.organizer_fee)) }}
          </span>
          <span v-else-if="k.priceError" class="ev-tag ev-tag-warn" v-hint="k.priceError">{{ $t('evV3.priceNeedOverride') }}</span>
        </div>
        <div class="ev-review-body" v-if="!k.alreadyInEvent && !k.programBlocked">
          <label class="ev-radio">
            <input type="radio" :name="'md' + k.studentid" value="deduct" v-model="k.mode" :disabled="k.deductLocked">
            {{ $t('eventMgr.modeDeduct') }}
          </label>
          <label class="ev-radio">
            <input type="radio" :name="'md' + k.studentid" value="free" v-model="k.mode"> {{ $t('evV3.modeFree') }}
          </label>
          <label class="ev-radio">
            {{ $t('evReg.entryLevel') }}
            <!-- ค่าเริ่มต้น = เลเวลปัจจุบันของเด็กอยู่แล้ว · ช่องว่าง = เด็กยังไม่มีเลเวลในระบบ บอกไว้ในช่องเลย -->
            <input class="id-input" type="number" min="0" max="999" step="0.01" style="width:120px" autocomplete="off"
              v-model="k.levelInput" :placeholder="$t('levelRes.noLevel')" @change="recheckLevel(k)">
            <span v-if="k.currentLevel && !lvSame(k.currentLevel, k.levelInput)" class="ev-dim" style="font-size:12px">({{ $t('evReg.nowLevel', { n: k.currentLevel }) }})</span>
          </label>
        </div>
      </div>

      <template #footer>
        <button class="id-btn id-btn-ghost" :disabled="saving" @click="addOpen = false">{{ $t('btn.cancel') }}</button>
        <button v-if="review.length === 0" class="id-btn id-btn-primary" :disabled="selectedKids.length === 0 || fetching" @click="runReview">
          {{ $t('evV3.previewBtn') }} <span class="mdi mdi-arrow-right"></span>
        </button>
        <button v-else class="id-btn id-btn-primary" :disabled="saving || addPayload.length === 0" @click="submitParticipants">
          <span class="mdi mdi-check"></span> {{ $t('evV3.saveParticipants', { n: addPayload.length }) }}
        </button>
      </template>
    </id-modal>

    <!-- ===== แก้การชำระของใบสมัคร ===== -->
    <id-modal v-model="regOpen" size="md" icon="mdi-cash-multiple" :title="$t('evReg.editTitle')" persistent>
      <div class="form-grid">
        <div class="field full">
          <label class="evreg-flag" @click="regForm.override = !regForm.override">
            <span class="id-check" :class="{ on: regForm.override }"><span class="mdi mdi-check"></span></span>
            {{ $t('evReg.overrideToggle') }}
          </label>
        </div>
        <div class="field">
          <label>{{ $t('evReg.entryLevel') }}</label>
          <input class="id-input" type="number" min="0" max="999" step="0.01" v-model="regForm.level" autocomplete="off">
        </div>
        <div v-if="!lvSame(regForm.level, regForm.origLevel) && !regForm.override && !regForm.wasOverridden" class="field full field-desc ev-dim">
          <span class="mdi mdi-information-outline"></span> {{ $t('evReg.entryLevelNote') }}
        </div>
        <div v-if="regForm.wasOverridden && !regForm.override" class="field full field-desc ev-dim">
          <span class="mdi mdi-information-outline"></span> {{ $t('evReg.resetNote') }}
        </div>
        <template v-if="regForm.override">
          <div class="field"><label>{{ $t('evCfg.gymFee') }}</label>
            <id-money v-model="regForm.gym_fee"></id-money></div>
          <div class="field"><label>{{ $t('evCfg.organizerFee') }}</label>
            <id-money v-model="regForm.organizer_fee"></id-money></div>
        </template>
        <div class="field full">
          <label class="evreg-flag" @click="regForm.paid = !regForm.paid">
            <span class="id-check" :class="{ on: regForm.paid }"><span class="mdi mdi-check"></span></span>
            {{ $t('evReg.markPaid') }}
          </label>
        </div>
        <template v-if="regForm.paid">
          <div class="field"><label>{{ $t('customerCourse.paymentMethod') }} <span class="req">*</span></label>
            <id-select v-model="regForm.paymentmethod"
              :options="[{ value: 'cash', label: $t('customerCourse.paymentCash') }, { value: 'transfer', label: $t('customerCourse.paymentTransfer') }]"
              :placeholder="$t('customerCourse.selectPayment')"></id-select>
          </div>
          <div class="field"><label>{{ $t('evReg.paydate') }} <span class="req">*</span></label>
            <id-date v-model="regForm.paydate"></id-date></div>
        </template>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="regOpen = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!canSaveReg" @click="saveReg">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- ===== ยกเลิกใบสมัคร (ใบจ่ายแล้วต้องระบุ คืน/ไม่คืน) ===== -->
    <id-modal v-model="regCancelOpen" size="sm" icon="mdi-cash-refund" :title="$t('evReg.cancelTitle')" persistent>
      <p style="margin:0 0 10px">{{ regCancelMsg }}</p>
      <!-- ใบที่มีเงิน: ต้องเลือก "เจตนา" ก่อน (เจ้าของเคาะ 12 ส.ค. จาก mockup) — ปุ่มเดิมถูกใช้
           2 เจตนาที่คนละเรื่องกัน แล้วบังคับตอบคำถามเรื่องเงินทั้งที่เคส "ลบแล้วลงใหม่" ไม่ใช่เรื่องเงิน -->
      <template v-if="regCancel.hasMoney">
        <!-- ใบที่รับเงินมาแล้วไม่มีตัวเลือก "กรอกผิด" (เจ้าของเคาะ 13 ส.ค. — เงินต้องย้อนกลับหมด)
             ถ้าเลือกได้ เงินก้อนนั้นจะหายจากรายงาน คิวคืนเงิน และ roster พร้อมกัน ไม่มีใครตามเจอ -->
        <div class="ev-radios ev-kinds">
          <label v-for="k in cancelKinds" :key="k" class="ev-kind"
            :class="{ on: regCancel.kind === k }">
            <input type="radio" name="evCancelKind" :value="k" v-model="regCancel.kind">
            <span>
              <span class="strong">{{ $t('evV6.kind_' + k) }}</span>
              <span class="t-cap">{{ $t('evV6.kindHint_' + k) }}</span>
            </span>
          </label>
        </div>
        <!-- ไม่มีการคืนบางส่วน (เจ้าของเคาะ 13 ส.ค.) → ยอดคืนล็อกเป็นยอดที่ชำระไว้ ไม่ให้พิมพ์
             (ถ้าพิมพ์ได้ ส่วนต่างที่ยิมเก็บไว้จะไม่ปรากฏในรายงานเลย — ตั้งใจไม่คืนต้องเลือก "ไม่คืนเงิน") -->
        <div v-if="regCancel.kind === 'refund'" class="form-grid" style="margin-top:10px">
          <div class="field"><label>{{ $t('evReg.refundAmount') }}</label>
            <input class="id-input" :value="$money(regCancel.total)" readonly disabled>
            <span class="fhint">{{ $t('evV6.refundFullOnly') }}</span></div>
          <div class="field"><label>{{ $t('evReg.refundDate') }} <span class="req">*</span></label>
            <id-date v-model="regCancel.refund_date"></id-date></div>
        </div>
        <!-- ไม่คืนเงิน + มีขาผู้จัด → ต้องบอกว่าขาผู้จัดยังต้องนำส่งไหม (อีเวนต์ยิมจัดเองไม่ถาม) -->
        <template v-if="regCancel.kind === 'forfeit' && regCancel.hasOrganizer">
          <div class="modal-sec mt"><span class="mdi mdi-send"></span> {{ $t('evV6.organizerSection', { amount: $money(regCancel.organizer_fee) }) }}</div>
          <div class="ev-radios ev-kinds">
            <label class="ev-kind" :class="{ on: regCancel.organizer_kept === 0 }">
              <input type="radio" name="evOrgKept" :value="0" v-model="regCancel.organizer_kept">
              <span><span class="strong">{{ $t('evV6.orgRemit') }}</span>
              <span class="t-cap">{{ $t('evV6.orgRemitHint', { amount: $money(regCancel.gym_fee) }) }}</span></span>
            </label>
            <label class="ev-kind" :class="{ on: regCancel.organizer_kept === 1 }">
              <input type="radio" name="evOrgKept" :value="1" v-model="regCancel.organizer_kept">
              <span><span class="strong">{{ $t('evV6.orgKeep') }}</span>
              <span class="t-cap">{{ $t('evV6.orgKeepHint', { amount: $money(Number(regCancel.gym_fee) + Number(regCancel.organizer_fee)) }) }}</span></span>
            </label>
          </div>
        </template>
        <div class="fhint ev-tip"><span class="mdi mdi-lightbulb-outline"></span> {{ $t('evV6.editPaymentTip') }}</div>
      </template>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="regCancelOpen = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)"
          :disabled="!canConfirmRegCancel" @click="confirmRegCancel">
          <span class="mdi mdi-check"></span> {{ $t('btn.confirm') }}</button>
      </template>
    </id-modal>

    <!-- ===== คิวรอบันทึกการคืนเงิน (เฟส 5) — โผล่เฉพาะเมื่อมีของค้างจริง ===== -->
    <div v-if="refundQueue.length" class="scard" style="padding:16px;margin-top:16px">
      <div class="modal-sec" style="margin-top:0">
        <span class="mdi mdi-cash-refund"></span> {{ $t('evV5.refundQueueTitle') }}
        <span class="badge badge-warning" style="margin-left:8px">{{ refundQueue.length }}</span>
        <span class="ev-dim" style="margin-left:auto">{{ $t('evV5.refundQueueTotal', { amount: $money(refundQueueAmount) }) }}</span>
      </div>
      <div class="fhint" style="margin-bottom:10px">{{ $t('evV5.refundQueueHint') }}</div>
      <div v-for="r in refundQueue" :key="r.registrationid" class="ev-refund-row">
        <div style="flex:1;min-width:0">
          <div class="strong">{{ r.eventname }} <span class="ev-dim">· {{ formatDate(r.eventdate) }}</span></div>
          <div class="t-cap">
            <span class="mdi mdi-account"></span> {{ r.nickname || r.studentid }} ·
            {{ $baht(r.amount) }} ·
            {{ $t('evV5.paidOn', { date: formatDate(r.paydate) }) }}
            <template v-if="r.paymentmethod"> · {{ r.paymentmethod === 'cash' ? $t('customerCourse.paymentCash') : $t('customerCourse.paymentTransfer') }}</template>
          </div>
        </div>
        <a v-if="r.slip_image_url" :href="r.slip_image_url" target="_blank" rel="noopener" class="id-btn id-btn-ghost id-btn-sm">
          <span class="mdi mdi-image-search"></span> {{ $t('customerCourse.viewSlip') }}
        </a>
        <button class="id-btn id-btn-primary id-btn-sm" @click="openRefundDialog(r)">
          <span class="mdi mdi-cash-refund"></span> {{ $t('evV5.recordRefund') }}
        </button>
      </div>
    </div>

    <!-- ===== ยกเลิกทั้งงาน: ยืนยันชั้นที่ 2 พร้อมสรุปผลกระทบที่ backend คำนวณให้ ===== -->
    <id-modal v-model="wholeOpen" size="md" icon="mdi-calendar-remove-outline"
      :title="$t('evV5.cancelWholeTitle')" :subtitle="wholeEvent ? wholeEvent.eventname : ''" persistent>
      <div class="danger-txt"><span class="mdi mdi-alert-circle-outline"></span>
        <span>{{ $t('evV5.cancelWholeWarn') }}</span></div>
      <div v-if="wholeSummary" class="ev-sum">
        <div class="row" style="justify-content:space-between"><span>{{ $t('evV5.sumRegs') }}</span><b>{{ wholeSummary.registrations }}</b></div>
        <div class="row" style="justify-content:space-between"><span>{{ $t('evV5.sumPaid') }}</span>
          <b>{{ wholeSummary.paidCount }} · {{ $baht(wholeSummary.paidAmount) }}</b></div>
        <div class="row" style="justify-content:space-between"><span>{{ $t('evV5.sumQuota') }}</span><b>{{ wholeSummary.quotaToReturn }}</b></div>
      </div>
      <div class="fhint" style="margin-top:10px">{{ $t('evV5.cancelWholeNote') }}</div>
      <template #footer>
        <button class="id-btn id-btn-ghost" :disabled="saving" @click="wholeOpen = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" :disabled="saving" @click="doCancelWholeEvent">
          <span class="mdi mdi-calendar-remove-outline"></span> {{ $t('evV5.cancelWholeConfirm') }}
        </button>
      </template>
    </id-modal>

    <!-- ===== บันทึกการคืนเงินของใบที่ยกเลิกแล้ว ===== -->
    <id-modal v-model="refundOpen" size="md" icon="mdi-cash-refund" :title="$t('evV5.recordRefundTitle')"
      :subtitle="refundTarget ? (refundTarget.nickname + ' · ' + refundTarget.eventname) : ''" persistent>
      <template v-if="refundTarget">
        <div class="row" style="justify-content:space-between;font-size:15px;font-weight:700">
          <span>{{ $t('evV5.paidAmount') }}</span><span>{{ $baht(refundTarget.amount) }}</span>
        </div>
        <div class="modal-sec mt"><span class="mdi mdi-cash-multiple"></span> {{ $t('evV5.refundDecision') }}</div>
        <!-- 3 ทางเดียวกับ dialog ยกเลิก (radio group จริง เลือกได้อย่างเดียวเสมอ) -->
        <!-- 3 ทางเดียวกับ dialog ยกเลิก (radio group จริง เลือกได้อย่างเดียวเสมอ) -->
        <div class="ev-radios ev-kinds">
          <label v-for="k in ['refund', 'forfeit', 'mistake']" :key="k" class="ev-kind"
            :class="{ on: refundForm.kind === k }">
            <input type="radio" name="evRefundKind" :value="k" v-model="refundForm.kind">
            <span><span class="strong">{{ $t('evV6.kind_' + k) }}</span>
            <span class="t-cap">{{ $t('evV6.kindHint_' + k) }}</span></span>
          </label>
        </div>
        <div v-if="refundForm.kind === 'refund'" class="form-grid" style="margin-top:10px">
          <div class="field">
            <label>{{ $t('evReg.refundAmount') }}</label>
            <input class="id-input" :value="$money(refundTarget.amount)" readonly disabled>
            <span class="fhint">{{ $t('evV6.refundFullOnly') }}</span>
          </div>
          <div class="field">
            <label>{{ $t('evReg.refundDate') }} <span class="req">*</span></label>
            <id-date v-model="refundForm.date"></id-date>
          </div>
          <div class="field full"><div class="fhint">{{ $t('evV5.refundOutsideHint') }}</div></div>
        </div>
        <template v-if="refundForm.kind === 'forfeit' && Number(refundTarget.organizer_fee) > 0">
          <div class="modal-sec mt"><span class="mdi mdi-send"></span> {{ $t('evV6.organizerSection', { amount: $money(refundTarget.organizer_fee) }) }}</div>
          <div class="ev-radios ev-kinds">
            <label class="ev-kind" :class="{ on: refundForm.organizer_kept === 0 }">
              <input type="radio" name="evRefundOrg" :value="0" v-model="refundForm.organizer_kept">
              <span><span class="strong">{{ $t('evV6.orgRemit') }}</span>
              <span class="t-cap">{{ $t('evV6.orgRemitHint', { amount: $money(refundTarget.gym_fee) }) }}</span></span>
            </label>
            <label class="ev-kind" :class="{ on: refundForm.organizer_kept === 1 }">
              <input type="radio" name="evRefundOrg" :value="1" v-model="refundForm.organizer_kept">
              <span><span class="strong">{{ $t('evV6.orgKeep') }}</span>
              <span class="t-cap">{{ $t('evV6.orgKeepHint', { amount: $money(refundTarget.amount) }) }}</span></span>
            </label>
          </div>
        </template>
        <div v-if="refundForm.kind === 'mistake'" class="fhint" style="margin-top:8px">{{ $t('evV6.kindHint_mistake') }}</div>
      </template>
      <template #footer>
        <button class="id-btn id-btn-ghost" :disabled="saving" @click="refundOpen = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="saving || !canSaveRefund" @click="saveRefund">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}
        </button>
      </template>
    </id-modal>

    <!-- ===== บันทึกนำส่งเงินผู้จัดภายนอก (นำส่งได้หลายครั้ง) ===== -->
    <id-modal v-model="remitOpen" size="md" icon="mdi-bank-transfer-out" :title="$t('evRemit.title')"
      :subtitle="remitEvent ? remitEvent.eventname : ''" persistent>
      <template v-if="remit">
        <div class="ev-sum">
          <div class="row" style="justify-content:space-between"><span>{{ $t('evRemit.collected') }}</span><b>{{ $baht(remit.collected) }}</b></div>
          <div class="row" style="justify-content:space-between"><span>{{ $t('evRemit.remitted') }}</span><b>{{ $baht(remit.remitted) }}</b></div>
          <div class="row" style="justify-content:space-between"><span>{{ $t('evRemit.outstanding') }}</span>
            <b :style="{ color: remit.outstanding > 0 ? 'var(--c-warning)' : remit.outstanding < 0 ? 'var(--c-error)' : 'var(--c-success)' }">{{ $baht(remit.outstanding) }}</b></div>
        </div>
        <div class="modal-sec mt"><span class="mdi mdi-history"></span> {{ $t('evRemit.history') }}</div>
        <div v-if="!remit.remits.length" class="ev-dim">{{ $t('evRemit.none') }}</div>
        <div v-for="m in remit.remits" :key="m.id" class="ev-refund-row">
          <div style="flex:1;min-width:0">
            <div class="strong">{{ $baht(m.amount) }} <span class="ev-dim">· {{ formatDate(m.remit_date) }}</span></div>
            <div class="t-cap">{{ $t('evRemit.by', { name: m.createby }) }}<template v-if="m.note"> · {{ m.note }}</template></div>
          </div>
          <span class="mdi mdi-delete-outline" style="color:var(--c-error);cursor:pointer;font-size:20px"
            v-hint="$t('table.delete')" :aria-label="$t('table.delete')" @click="askDeleteRemit(m)"></span>
        </div>
        <template v-if="remit.outstanding > 0">
          <div class="modal-sec mt"><span class="mdi mdi-plus-circle-outline"></span> {{ $t('evRemit.add') }}</div>
          <div class="fhint" style="margin-bottom:8px">{{ $t('evRemit.hint') }}</div>
          <div class="form-grid">
            <div class="field">
              <label>{{ $t('evRemit.amount') }} <span class="req">*</span></label>
              <id-money v-model="remitForm.amount"></id-money>
            </div>
            <div class="field">
              <label>{{ $t('evRemit.date') }} <span class="req">*</span></label>
              <id-date v-model="remitForm.date"></id-date>
            </div>
            <div class="field full">
              <label>{{ $t('evRemit.note') }}</label>
              <input class="id-input" v-model="remitForm.note" maxlength="255" :placeholder="$t('evRemit.notePh')" autocomplete="off">
            </div>
          </div>
        </template>
        <div v-else-if="remit.outstanding < 0" class="danger-txt" style="margin-top:10px"><span class="mdi mdi-alert-circle-outline"></span>
          <span>{{ $t('evRemit.overpaid', { amount: $baht(-remit.outstanding) }) }}</span></div>
        <div v-else class="fhint" style="margin-top:10px"><span class="mdi mdi-check-circle-outline"></span> {{ $t('evRemit.allDone') }}</div>
      </template>
      <template #footer>
        <button class="id-btn id-btn-ghost" :disabled="saving" @click="remitOpen = false">{{ $t('btn.close') }}</button>
        <button v-if="remit && remit.outstanding > 0" class="id-btn id-btn-primary" :disabled="saving || !canSaveRemit" @click="saveRemit">
          <span class="mdi mdi-content-save"></span> {{ $t('evRemit.save') }}
        </button>
      </template>
    </id-modal>

    <!-- ===== ยืนยันแบบทั่วไป (ลบอีเวนต์ / เอาชื่อออกจากอีเวนต์รุ่นเก่า) ===== -->
    <id-modal v-model="confirmOpen" size="sm" icon="mdi-alert-outline" :title="$t('eventMgr.confirmTitle')" persistent>
      <div class="danger-txt"><span class="mdi mdi-alert-circle-outline"></span> <span>{{ confirmMsg }}</span></div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="confirmOpen = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmGo">
          <span class="mdi mdi-check"></span> {{ $t('btn.confirm') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios'
import EventConfigForm from './EventConfigForm.vue'
import EventLevelResults from './EventLevelResults.vue'
import { displayYear } from '@/utils/locale'

// ฟอร์ม config ว่าง — สองสวิตช์ปิดทั้งคู่ (อีเวนต์ธรรมดา ไม่มีราคา ไม่เปิดรับสมัคร)
const blankCfg = () => ({
  pricing: {
    enabled: false, price_mode: 'flat', fallback_gym_fee: '', fallback_organizer_fee: '',
    prices: [{ level_value: '', age_min: '', age_max: '', gym_fee: '', organizer_fee: '' }],
  },
  registration: { enabled: false, reg_open: '', reg_close: '', self_register: false, program_restricted: false },
})

// เทียบเลเวลแบบตัวเลข (5 = 5.00) · ไม่ใช่ตัวเลข (เช่น L.1 ของเก่า) เทียบเป็นข้อความ
function lvSame(a, b) {
  const x = String(a ?? '').trim(), y = String(b ?? '').trim()
  if (x !== '' && y !== '' && Number.isFinite(Number(x)) && Number.isFinite(Number(y))) return Number(x) === Number(y)
  return x === y
}

export default {
  name: 'EventManager',
  components: { EventConfigForm, EventLevelResults },
  data() {
    return {
      events: [], loadingEvents: false,
      courseLookup: [], studentLookup: [],
      // สร้าง/แก้ไขอีเวนต์
      eventDialog: false, editingId: null, saving: false, fetching: false,
      form: { eventdate: '', starttime: '', eventname: '', courseid: '', maxperson: 100, note: '' },
      cfgForm: blankCfg(),
      // รายชื่อ
      rosterOpen: false, rosterEvent: null, rosterLegacy: false, roster: [],
      rosterTab: 'active', cancelledList: [], levelResults: null,
      // เพิ่มผู้เข้าร่วม
      addOpen: false, kidSearch: '', selectedKids: [], review: [],
      // ชั้นเงินของใบสมัคร
      regOpen: false, regTargetId: null,
      regForm: { override: false, gym_fee: '', organizer_fee: '', paid: false, paymentmethod: '', paydate: '', level: '', origLevel: '' },
      regCancelOpen: false,
      regCancelMsg: '',
      // kind: mistake | refund | forfeit (ใบที่ไม่มีเงินไม่ต้องเลือก → ส่ง mistake อัตโนมัติ)
      regCancel: {
        id: null, hasMoney: false, hasOrganizer: false, gym_fee: 0, organizer_fee: 0,
        kind: 'mistake', refund_amount: '', refund_date: '', organizer_kept: 0,
      },
      confirmOpen: false, confirmMsg: '', confirmAction: null,
      // เฟส 5 — ยกเลิกทั้งงาน + คิวบันทึกการคืนเงิน
      wholeOpen: false, wholeEvent: null, wholeSummary: null,
      refundQueue: [], refundQueueAmount: '0.00',
      refundOpen: false, refundTarget: null,
      refundForm: { kind: 'refund', amount: '', date: '', organizer_kept: 0 },
      // บันทึกนำส่งเงินผู้จัด
      remitOpen: false, remitEvent: null, remit: null,
      remitForm: { amount: '', date: '', note: '' },
    }
  },
  computed: {
    // ผู้จัดการ (usertype '0') เท่านั้นที่จัดการอีเวนต์ย้อนหลังได้ — backend บังคับด้วย eventPastGuard
    // ตรงนี้ทำให้หน้าจอ "บอกก่อน" แทนที่จะปล่อยให้กรอกจบแล้วเด้ง error
    isHeadUser() {
      const u = this.$store.getters.getUserdata
      return !!u && String(u.usertype) === '0'
    },
    // 'YYYY-MM-DD' ของวันนี้ — ประกอบเองจาก get*() ของ local time ห้ามใช้ toISOString() (เลื่อน -1 วัน)
    todayISO() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    rosterIsPast() {
      const d = this.rosterEvent && this.rosterEvent.eventdate
      return !!d && String(d).slice(0, 10) < this.todayISO
    },
    programOptions() {
      return [{ value: '', label: this.$t('evV3.allPrograms') },
        ...this.courseLookup.map((c) => ({ value: c.courseid, label: c.coursename }))]
    },
    // แถวสำหรับ grid — เติมฟิลด์ที่ filter/ค้นหาใช้ (ปี, ชื่อโปรแกรม)
    eventRows() {
      return this.events.map((e) => ({
        ...e,
        year: String(e.eventdate || '').slice(0, 4),
        programLabel: e.coursename || this.$t('evV3.allPrograms'),
      }))
    },
    gridFilters() {
      const years = [...new Set(this.eventRows.map((r) => r.year))].filter(Boolean).sort().reverse()
      const programs = [...new Set(this.eventRows.map((r) => r.programLabel))].filter(Boolean).sort()
      return [
        { key: 'year', label: this.$t('evV3.filterYear'), options: years.map((y) => ({ value: y, label: String(displayYear(Number(y))) })) },
        { key: 'programLabel', label: this.$t('evV3.filterProgram'), options: programs.map((p) => ({ value: p, label: p })) },
      ]
    },
    eventColumns() {
      return [
        { title: this.$t('eventMgr.date'), key: 'eventdate' },
        { title: this.$t('eventMgr.label'), key: 'eventname' },
        { title: this.$t('evV3.program'), key: 'programLabel' },
        { title: this.$t('eventMgr.kids'), key: 'kids', align: 'center', sortable: false },
        { title: this.$t('evV3.unpaidCol'), key: 'unpaid', align: 'center', sortable: false },
        { title: this.$t('evV3.amountCol'), key: 'total_amount', align: 'right', sortable: false },
        { title: this.$t('eventMgr.roster'), key: 'roster', align: 'center', sortable: false },
        { title: this.$t('levelRes.colHeader'), key: 'levelchg', align: 'center', sortable: false },
        { title: this.$t('evRemit.colHeader'), key: 'remit', align: 'center', sortable: false },
        { title: this.$t('table.edit'), key: 'edit', align: 'center', sortable: false },
        { title: this.$t('table.delete'), key: 'delete', align: 'center', sortable: false },
      ]
    },
    filteredLookup() {
      const q = this.kidSearch.trim().toLowerCase()
      const chosen = new Set(this.selectedKids.map((s) => s.studentid))
      return this.studentLookup
        .filter((s) => !chosen.has(s.studentid) && (!q || String(s.name).toLowerCase().includes(q)))
        .slice(0, 12)
    },
    // เลือก "คืนเงิน" เหลือกรอกแค่วันที่ — ยอดคืน = ยอดที่ชำระไว้เสมอ (ไม่มีคืนบางส่วน)
    canSaveRefund() {
      if (this.refundForm.kind !== 'refund') return true
      return !!this.refundForm.date
    },
    // ใบที่ไม่มีเงิน = ยืนยันได้เลย · ใบที่มีเงินและเลือก "คืนเงิน" ต้องกรอกยอด+วันที่ก่อน
    // 3 ทางเสมอ รวมใบที่จ่ายเงินแล้ว — "กรอกผิด" ต้องลบได้เหมือนไม่เคยเกิดขึ้น (เหมือนลบคอร์สเรียน)
    // ตาข่ายของเคสนี้คือแท็บ "ประวัติการยกเลิก" ซึ่งเก็บเฉพาะการยกเลิกที่เป็นธุรกรรมจริง
    cancelKinds() {
      return ['mistake', 'refund', 'forfeit']
    },
    canConfirmRegCancel() {
      const c = this.regCancel
      if (!c.hasMoney) return true
      if (c.kind !== 'refund') return true
      return !!c.refund_date   // ยอดคืน = ยอดที่ชำระไว้เสมอ (ไม่มีคืนบางส่วน) เหลือแค่วันที่
    },
    canSaveRemit() {
      const a = Number(this.remitForm.amount)
      return a > 0 && !!this.remitForm.date && !!this.remit && a <= this.remit.outstanding + 0.004
    },
    canSaveEvent() {
      return !!(this.form.eventdate && String(this.form.eventname).trim())
    },
    canSaveReg() {
      const f = this.regForm
      if (f.override && (f.gym_fee === '' || f.organizer_fee === '')) return false
      if (f.paid && (!f.paymentmethod || !f.paydate)) return false
      return true
    },
    // เด็กที่พร้อมบันทึกจริง (ตัดคนที่มีชื่ออยู่แล้ว/โปรแกรมไม่ตรง)
    addPayload() {
      return this.review
        .filter((k) => !k.alreadyInEvent && !k.programBlocked)
        .map((k) => ({
          studentid: k.studentid,
          mode: k.mode === 'deduct' ? 'deduct' : 'free',
          ...(k.levelInput !== '' && k.levelInput != null && !lvSame(k.levelInput, k.currentLevel) ? { level: String(k.levelInput) } : {}),
        }))
    },
  },
  created() { this.init() },
  methods: {
    lvSame,
    hdr() { return { headers: { Authorization: `Bearer ${this.$store.getters.getToken}` } } },
    async init() {
      try {
        await axios.post(this.baseURL + '/verifyToken', {}, this.hdr())
        await Promise.all([this.loadEvents(), this.loadLookups(), this.loadRefundQueue()])
      } catch (e) { this.$emit('onErrorHandler', e.message) }
    },
    async loadEvents() {
      this.loadingEvents = true
      try {
        // ไม่ส่ง from/to = แสดงทั้งหมด (เจ้าของสั่งเลิกกรอบ 90 วัน) — filter ทำฝั่ง grid
        const res = await axios.post(this.baseURL + '/getEvents', {}, this.hdr())
        this.events = res.data.results || []
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.loadingEvents = false }
    },
    async loadLookups() {
      const [c, s] = await Promise.all([
        axios.get(this.baseURL + '/courseLookup', this.hdr()),
        axios.post(this.baseURL + '/studentLookup', {}, this.hdr()),
      ])
      this.courseLookup = c.data.results || []
      this.studentLookup = s.data.results || []
    },
    formatDate(d) {
      if (!d) return ''
      return this.$formatShortDate(d)
    },
    hasMoney(r) { return Number(r.gym_fee) > 0 || Number(r.organizer_fee) > 0 || Number(r.paid) === 1 },
    // ---------- สร้าง / แก้ไขอีเวนต์ ----------
    async openEventDialog(ev) {
      this.editingId = ev ? ev.eventid : null
      this.cfgForm = blankCfg()
      if (ev) {
        this.form = {
          eventdate: ev.eventdate, starttime: ev.starttime || '', eventname: ev.eventname,
          courseid: ev.courseid || '', maxperson: ev.maxperson || 100, note: '',
        }
        // โหลดค่าคอนฟิกให้ครบก่อนเปิด (มีอยู่แล้ว) — เพิ่มม่านโหลดให้ผู้ใช้รู้ว่ากดติดแล้ว
        this.$emit('onLoading', true)
        try {
          await this.loadDetail(ev.eventid)
        } finally {
          this.$emit('onLoading', false)
        }
      } else {
        this.form = { eventdate: '', starttime: '', eventname: this.$t('eventMgr.defaultLabel'), courseid: '', maxperson: 100, note: '' }
      }
      this.eventDialog = true
    },
    async loadDetail(eventid) {
      try {
        const res = await axios.post(this.baseURL + '/getEventDetail', { eventid }, this.hdr())
        if (!res.data.success) return
        const e = res.data.event
        this.form.note = e.note || ''
        this.cfgForm = {
          pricing: {
            enabled: Number(e.pricing_enabled) === 1,
            price_mode: e.price_mode || 'flat',
            fallback_gym_fee: e.fallback_gym_fee === null ? '' : e.fallback_gym_fee,
            fallback_organizer_fee: e.fallback_organizer_fee === null ? '' : e.fallback_organizer_fee,
            prices: (res.data.prices.length ? res.data.prices : blankCfg().pricing.prices).map((p) => ({
              level_value: p.level_value || '',
              age_min: p.age_min === null || p.age_min === undefined ? '' : p.age_min,
              age_max: p.age_max === null || p.age_max === undefined ? '' : p.age_max,
              gym_fee: p.gym_fee || '', organizer_fee: p.organizer_fee || '',
            })),
          },
          registration: {
            enabled: Number(e.registration_enabled) === 1,
            reg_open: e.reg_open || '', reg_close: e.reg_close || '',
            self_register: Number(e.self_register) === 1,
            program_restricted: Number(e.program_restricted) === 1,
          },
        }
      } catch (e) { this.$emit('onErrorHandler', e.message) }
    },
    // แปลง cfgForm → payload ของ backend · สวิตช์ปิด = ส่ง null ชัด ๆ (ตั้งใจล้าง ไม่ใช่ไม่แตะ)
    cfgPayload() {
      const p = this.cfgForm.pricing, r = this.cfgForm.registration
      return {
        pricing: p.enabled ? {
          price_mode: p.price_mode,
          fallback_gym_fee: p.fallback_gym_fee === '' ? null : p.fallback_gym_fee,
          fallback_organizer_fee: p.fallback_organizer_fee === '' ? null : p.fallback_organizer_fee,
          prices: p.prices.map((x) => ({
            level_value: x.level_value === '' || x.level_value == null ? null : String(x.level_value),
            age_min: x.age_min === '' ? null : Number(x.age_min),
            age_max: x.age_max === '' ? null : Number(x.age_max),
            gym_fee: x.gym_fee, organizer_fee: x.organizer_fee === '' ? null : x.organizer_fee,
          })),
        } : null,
        registration: r.enabled ? {
          reg_open: r.reg_open || null, reg_close: r.reg_close || null,
          self_register: !!r.self_register, program_restricted: !!r.program_restricted,
        } : null,
      }
    },
    async saveEvent() {
      this.saving = true
      this.$emit('onLoading', true)
      try {
        const body = {
          eventname: String(this.form.eventname).trim(),
          eventdate: this.form.eventdate,
          starttime: this.form.starttime || '',
          courseid: this.form.courseid === '' ? null : this.form.courseid,
          maxperson: Number(this.form.maxperson) || 100,
          note: this.form.note || '',
          ...this.cfgPayload(),
        }
        if (this.editingId) body.eventid = this.editingId
        const res = await axios.post(this.baseURL + (this.editingId ? '/updateEvent' : '/addEvent'), body, this.hdr())
        if (res.data.success) {
          this.eventDialog = false
          this.$emit('onInfoHandler', res.data.message)
          await this.loadEvents()
        } else this.$emit('onErrorHandler', res.data.message)
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.saving = false; this.$emit('onLoading', false) }
    },
    // ---------- เฟส 5: ยกเลิกทั้งงาน ----------
    // ชั้นที่ 1 = ขอสรุปผลกระทบจาก backend (ไม่ส่ง confirm) แล้วเอามาโชว์ในกล่องยืนยันชั้นที่ 2
    // จงใจไม่คำนวณสรุปเองฝั่งหน้าจอ: ตัวเลขที่ผู้ใช้เห็นต้องมาจากที่เดียวกับที่ลงมือทำจริง
    async askCancelWholeEvent() {
      const ev = { eventid: this.editingId, eventname: String(this.form.eventname).trim() }
      this.saving = true
      try {
        const res = await axios.post(this.baseURL + '/cancelWholeEvent', { eventid: ev.eventid }, this.hdr())
        if (res.data.requireConfirm) {
          this.wholeEvent = ev
          this.wholeSummary = res.data.summary || null
          this.wholeOpen = true
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('common.serverError'))
        }
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.saving = false }
    },
    async doCancelWholeEvent() {
      this.saving = true
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/cancelWholeEvent',
          { eventid: this.wholeEvent.eventid, confirm: true }, this.hdr())
        if (res.data.success) {
          this.wholeOpen = false
          this.eventDialog = false
          this.$emit('onInfoHandler', res.data.message)
          await this.loadEvents()
          await this.loadRefundQueue()
        } else this.$emit('onErrorHandler', res.data.message)
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.saving = false; this.$emit('onLoading', false) }
    },
    // ---------- เฟส 5: คิวบันทึกการคืนเงิน ----------
    async loadRefundQueue() {
      try {
        const res = await axios.post(this.baseURL + '/getEventRefundQueue', {}, this.hdr())
        this.refundQueue = (res.data && res.data.results) || []
        this.refundQueueAmount = (res.data && res.data.amount) || '0.00'
      } catch (e) { this.refundQueue = [] }
    },
    openRefundDialog(r) {
      this.refundTarget = r
      // ค่าตั้งต้น: คืนเต็มยอดในวันนี้ (เคสที่พบบ่อยสุดของคิวนี้) · ขาผู้จัดตั้งต้น "ยังต้องนำส่ง"
      this.refundForm = { kind: 'refund', amount: this.$moneyInput(r.amount), date: this.todayISO, organizer_kept: 0 }
      this.refundOpen = true
    },
    async saveRefund() {
      this.saving = true
      this.$emit('onLoading', true)
      try {
        const body = { registrationid: this.refundTarget.registrationid, cancel_kind: this.refundForm.kind };
        if (this.refundForm.kind === 'refund') { body.refund_amount = this.refundTarget.amount; body.refund_date = this.refundForm.date }
        if (this.refundForm.kind === 'forfeit') { body.organizer_kept = this.refundForm.organizer_kept }
        const res = await axios.post(this.baseURL + '/recordEventRefund', body, this.hdr())
        if (res.data.success) {
          this.refundOpen = false
          this.$emit('onInfoHandler', res.data.message)
          await this.loadRefundQueue()
        } else this.$emit('onErrorHandler', res.data.message)
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.saving = false; this.$emit('onLoading', false) }
    },
    // ---------- บันทึกนำส่งเงินผู้จัด ----------
    // ดึงข้อมูลให้ครบก่อนเปิด dialog (กติกา "เปิดเมื่อข้อมูลครบ")
    async openRemit(ev) {
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/getEventOrganizerRemits', { eventid: ev.eventid }, this.hdr())
        if (!res.data.success) { this.$emit('onErrorHandler', res.data.message); return }
        this.remitEvent = ev
        this.applyRemit(res.data)
        this.remitOpen = true
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.$emit('onLoading', false) }
    },
    applyRemit(data) {
      this.remit = { collected: data.collected, remitted: data.remitted, outstanding: data.outstanding, remits: data.remits || [] }
      // ค่าตั้งต้น = ยอดค้างทั้งหมดในวันนี้ (เคสปกติคือโอนครั้งเดียวจบ) แอดมินแก้ได้
      this.remitForm = { amount: this.$moneyInput(Math.max(data.outstanding, 0)), date: this.todayISO, note: '' }
    },
    async saveRemit() {
      this.saving = true
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/addEventOrganizerRemit', {
          eventid: this.remitEvent.eventid, amount: this.remitForm.amount, remit_date: this.remitForm.date, note: this.remitForm.note,
        }, this.hdr())
        if (res.data.success) {
          // backend อาจตอบสำเร็จโดยไม่มียอดสรุป (อ่านหลัง commit พลาด) → ดึงใหม่แทนการเดาตัวเลข
          if (res.data.outstanding === undefined) await this.reloadRemit()
          else this.applyRemit(res.data)
          this.$emit('onInfoHandler', res.data.message)
        } else this.$emit('onErrorHandler', res.data.message)
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.saving = false; this.$emit('onLoading', false) }
    },
    async reloadRemit() {
      const r = await axios.post(this.baseURL + '/getEventOrganizerRemits', { eventid: this.remitEvent.eventid }, this.hdr())
      if (r.data.success) this.applyRemit(r.data)
    },
    askDeleteRemit(m) {
      this.confirmMsg = this.$t('evRemit.confirmDelete', { amount: this.$money(m.amount), date: this.formatDate(m.remit_date) })
      this.confirmAction = async () => {
        const res = await axios.post(this.baseURL + '/deleteEventOrganizerRemit',
          { eventid: this.remitEvent.eventid, remitid: m.id }, this.hdr())
        if (res.data.success) { this.applyRemit(res.data); this.$emit('onInfoHandler', res.data.message) }
        else this.$emit('onErrorHandler', res.data.message)
      }
      this.confirmOpen = true
    },
    askDeleteEvent(ev) {
      this.confirmMsg = this.$t('eventMgr.confirmDelete', { name: ev.eventname })
      this.confirmAction = async () => {
        const res = await axios.post(this.baseURL + '/deleteEvent', { eventid: ev.eventid }, this.hdr())
        if (res.data.success) { this.$emit('onInfoHandler', res.data.message); this.loadEvents() }
        else this.$emit('onErrorHandler', res.data.message)
      }
      this.confirmOpen = true
    },
    // ---------- รายชื่อ ----------
    // ป้ายเจตนาในแท็บประวัติ — NULL = ยกเลิกแล้วแต่ยังไม่บันทึกผลเงิน (อยู่ในคิวคืนเงิน)
    cancelKindLabel(c) {
      if (c.cancel_kind === 'refund') return this.$t('evV6.kindRefund')
      if (c.cancel_kind === 'forfeit') return this.$t('evV6.kindForfeit')
      return this.$t('evV6.kindPending')
    },
    cancelTagClass(c) {
      if (c.cancel_kind === 'refund') return 'ev-tag-info'
      if (c.cancel_kind === 'forfeit') return 'ev-tag-paid'
      return 'ev-tag-warn'
    },
    // keepTab: โหลดใหม่หลังบันทึกผลการแข่ง — อยู่แท็บเดิม ไม่เด้งกลับแท็บรายชื่อ
    // จากคอลัมน์ "เลื่อนเลเวล" ของตาราง — เปิดกล่องรายชื่อที่แท็บผลการแข่งตรง ๆ
    openResults(ev) {
      this.rosterTab = 'results'
      return this.openRoster(ev, true)
    },
    async openRoster(ev, keepTab) {
      this.rosterEvent = ev
      this.rosterLegacy = Number(ev.legacy) === 1
      this.$emit('onLoading', true)
      try {
        // ผลการแข่งโหลดคู่ขนานตั้งแต่เปิด (dialog เปิดเมื่อข้อมูลครบ · แท็บสลับได้ทันที)
        const [res, lr] = await Promise.all([
          axios.post(this.baseURL + '/getEventRoster',
            // legacydate: อีเวนต์รุ่นเก่า 1 คลาสถูกแตกเป็น 1 แถวต่อวัน — roster ต้องกรองเฉพาะวันที่กด
            { eventid: ev.eventid, legacy: this.rosterLegacy ? 1 : 0, legacydate: ev.legacydate || '' }, this.hdr()),
          this.rosterLegacy ? null
            : axios.post(this.baseURL + '/getEventLevelResults', { eventid: ev.eventid }, this.hdr()).catch(() => null),
        ])
        this.roster = res.data.results || []
        this.cancelledList = res.data.cancelled || []
        this.levelResults = lr && lr.data.success ? lr.data : null
        if (!keepTab) this.rosterTab = 'active'
        this.rosterOpen = true
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.$emit('onLoading', false) }
    },
    async checkin(r, flag) {
      try {
        // อีเวนต์ v3 มี endpoint เช็คชื่อของตัวเอง · รุ่นเก่ายังใช้รางของคลาสกลุ่ม
        const res = this.rosterLegacy
          ? await axios.post(this.baseURL + (flag ? '/checkinByAdmin' : '/undoCheckinByAdmin'),
            { reservationid: r.reservationid, studentid: r.studentid }, this.hdr())
          : await axios.post(this.baseURL + '/checkinEventRegistration',
            { registrationid: r.registrationid, checkedin: flag }, this.hdr())
        if (res.data.success) { r.checkedin = flag; this.loadEvents() }
        else { this.$emit('onErrorHandler', res.data.message); this.openRoster(this.rosterEvent) }
      } catch (e) { this.$emit('onErrorHandler', e.message) }
    },
    askRemove(r) {
      if (this.rosterLegacy) {
        // รุ่นเก่า: เอาชื่อออก = ยกเลิกใบจองบนรางเดิม (คืนโควตาตามกลไกเดิม)
        this.confirmMsg = this.$t('eventMgr.confirmRemove', { name: r.nickname })
        this.confirmAction = async () => {
          const res = await axios.post(this.baseURL + '/cancelBookingByAdmin',
            { reservationid: r.reservationid, studentid: r.studentid, courserefer: r.courserefer }, this.hdr())
          if (res.data.success) { this.roster = this.roster.filter((x) => x.reservationid !== r.reservationid); this.loadEvents() }
          else this.$emit('onErrorHandler', res.data.message)
        }
        this.confirmOpen = true
        return
      }
      const gym = Number(r.gym_fee) || 0
      const org = Number(r.organizer_fee) || 0
      const hasMoney = Number(r.paid) === 1 && gym + org > 0
      this.regCancel = {
        id: r.registrationid, hasMoney, hasOrganizer: org > 0, gym_fee: gym, organizer_fee: org,
        total: gym + org,
        // ค่าเริ่มต้น 'mistake' = เคสที่พบบ่อยสุด (ลงชื่อผิด) และเป็นทางที่ไม่แตะเงินเลย
        // ขาผู้จัดตั้งต้น 0 = ผู้จัดยังได้ส่วนของเขา (ปลอดภัยกว่า ไม่เผลอตัดหนี้ทิ้ง)
        kind: 'mistake',
        refund_amount: this.$moneyInput(gym + org), refund_date: this.todayISO, organizer_kept: 0,
      }
      this.regCancelMsg = hasMoney
        ? this.$t('evV6.cancelPaidMsg', { name: r.nickname, amount: this.$baht(gym + org) })
        : this.$t('evV3.cancelUnpaidMsg', { name: r.nickname, quota: r.quota_mode === 'deduct' ? this.$t('evV3.willRefundQuota') : '' })
      this.regCancelOpen = true
    },
    async confirmRegCancel() {
      const c = this.regCancel
      this.$emit('onLoading', true)
      try {
        const body = { id: c.id, cancel_kind: c.hasMoney ? c.kind : 'mistake' };
        if (c.hasMoney && c.kind === 'refund') { body.refund_amount = c.total; body.refund_date = c.refund_date }
        if (c.hasMoney && c.kind === 'forfeit') { body.organizer_kept = c.organizer_kept }
        const res = await axios.post(this.baseURL + '/cancelEventRegistration', body, this.hdr())
        if (res.data.success) {
          this.regCancelOpen = false
          this.$emit('onInfoHandler', res.data.message)
          await this.openRoster(this.rosterEvent)
          this.loadEvents()
        } else this.$emit('onErrorHandler', res.data.message)
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.$emit('onLoading', false) }
    },
    // ---------- เพิ่มผู้เข้าร่วม ----------
    openAddDialog() {
      this.selectedKids = []; this.kidSearch = ''; this.review = []
      this.addOpen = true
    },
    addKid(s) { this.selectedKids.push(s) },
    removeKid(id) {
      this.selectedKids = this.selectedKids.filter((s) => s.studentid !== id)
      this.review = this.review.filter((k) => k.studentid !== id)
    },
    async runReview() {
      if (this.fetching) return
      this.fetching = true
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/getEventParticipantCheck',
          { eventid: this.rosterEvent.eventid, studentids: this.selectedKids.map((s) => s.studentid) }, this.hdr())
        if (!res.data.success) { this.$emit('onErrorHandler', res.data.message); return }
        // levelInput = เลเวลที่ส่งแข่งรอบนี้ (ค่าเริ่มต้น = เลเวลปัจจุบันของเด็ก แก้ได้)
        this.review = (res.data.students || []).map((k) => ({ ...k, mode: k.suggestedMode, levelInput: k.level == null ? '' : String(k.level) }))
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.fetching = false; this.$emit('onLoading', false) }
    },
    // แอดมินแก้เลเวลที่ส่งแข่งของเด็กคนหนึ่ง → ขอราคาใหม่ตามเลเวลนั้น (หลังบ้านเป็นคนคิด)
    async recheckLevel(k) {
      const sent = String(k.levelInput ?? '')
      try {
        const res = await axios.post(this.baseURL + '/getEventParticipantCheck',
          { eventid: this.rosterEvent.eventid, studentids: [k.studentid], levels: { [k.studentid]: String(k.levelInput ?? '') } }, this.hdr())
        const fresh = res.data.success && (res.data.students || [])[0]
        // ตอบช้าแซงกัน/แอดมินแก้ต่อไปแล้ว → ทิ้งผลของเลเวลเก่า
        if (!fresh || String(k.levelInput ?? '') !== sent) return
        k.price = fresh.price
        k.priceError = fresh.priceError
      } catch (e) { this.$emit('onErrorHandler', e.message) }
    },
    async submitParticipants() {
      this.saving = true
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/addEventParticipants',
          { eventid: this.rosterEvent.eventid, students: this.addPayload }, this.hdr())
        if (res.data.success) {
          this.addOpen = false
          this.$emit('onInfoHandler', res.data.message)
          await this.openRoster(this.rosterEvent)
          this.loadEvents()
        } else this.$emit('onErrorHandler', res.data.message)  // transaction rollback — คงหน้าไว้ให้แก้
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.saving = false; this.$emit('onLoading', false) }
    },
    // ---------- ชั้นเงินของใบสมัคร ----------
    openRegEdit(r) {
      this.regTargetId = r.registrationid
      this.regForm = {
        // ใบที่ราคาถูกกำหนดเองอยู่แล้ว (ป้าย ✎) เปิดมาต้องติ๊กและเห็นยอดปัจจุบัน ให้ตรงกับป้าย
        override: Number(r.price_overridden) === 1, wasOverridden: Number(r.price_overridden) === 1, gym_fee: r.gym_fee || '', organizer_fee: r.organizer_fee || '',
        paid: Number(r.paid) === 1, paymentmethod: r.paymentmethod || '', paydate: r.paydate || '',
        // เลเวลที่ส่งแข่งรอบนี้ (entry level) — origLevel ไว้เทียบว่าแอดมินแก้หรือไม่ (ไม่แก้ = ไม่ส่ง)
        level: r.level == null ? '' : String(r.level), origLevel: r.level == null ? '' : String(r.level),
      }
      this.regOpen = true
    },
    async saveReg() {
      const f = this.regForm
      this.$emit('onLoading', true)
      try {
        const body = { id: this.regTargetId, paid: f.paid ? 1 : 0, paymentmethod: f.paymentmethod || '', paydate: f.paydate || '' }
        if (f.override) { body.gym_fee = f.gym_fee; body.organizer_fee = f.organizer_fee }
        else if (f.wasOverridden) body.reset_price = 1 // ติ๊กออก = กลับไปใช้ราคาตามเกณฑ์ (หลังบ้านคำนวณใหม่)
        if (!lvSame(f.level, f.origLevel)) body.level = String(f.level ?? '')
        const res = await axios.post(this.baseURL + '/updateEventRegistration', body, this.hdr())
        if (res.data.success) {
          this.regOpen = false
          this.$emit('onInfoHandler', res.data.message)
          await this.openRoster(this.rosterEvent)
          this.loadEvents()
        } else this.$emit('onErrorHandler', res.data.message)
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.$emit('onLoading', false) }
    },
    async uploadRegSlip(ev, r) {
      const file = ev.target.files && ev.target.files[0]
      ev.target.value = ''
      if (!file) return
      this.$emit('onLoading', true)
      try {
        const fd = new FormData()
        fd.append('slipImage', file)
        fd.append('registrationid', String(r.registrationid))
        const res = await fetch(this.baseURL + '/uploadEventRegistrationSlip', {
          method: 'POST', headers: { Authorization: `Bearer ${this.$store.getters.getToken}` }, body: fd,
        })
        const data = await res.json()
        if (data.success) {
          const sv = data.slipVerification
          if (sv && ['duplicate', 'wrong_receiver', 'amount_mismatch', 'failed'].includes(sv.status)) {
            this.$emit('onErrorHandler', `${this.$t('customerCourse.slipSaveWarn')}: ${sv.reason || sv.status}`)
          } else this.$emit('onInfoHandler', this.$t('evReg.slipSaved'))
          await this.openRoster(this.rosterEvent)
          this.loadEvents()
        } else this.$emit('onErrorHandler', data.message)
      } catch (e) { this.$emit('onErrorHandler', e.message) } finally { this.$emit('onLoading', false) }
    },
    async confirmGo() { const fn = this.confirmAction; this.confirmOpen = false; this.confirmAction = null; if (fn) await fn() },
  },
}
</script>

<style scoped>
/* ตัวเลือกเจตนาการยกเลิก (เฟส 6) — การ์ดกดเลือก ไฮไลต์ตัวที่เลือกด้วยกรอบสีแบรนด์ */
.ev-kinds { flex-direction: column; gap: 8px; }
/* แท็บใน dialog รายละเอียดอีเวนต์ (ผู้เข้าร่วม / ประวัติการยกเลิก) */
.ev-tabs { display: flex; gap: 6px; margin: 0 0 10px; border-bottom: 1px solid var(--c-border); }
.ev-tab { appearance: none; background: none; border: none; border-bottom: 2px solid transparent;
  padding: 7px 12px; font: inherit; font-weight: 600; color: var(--c-text-dim); cursor: pointer; }
.ev-lvchg { appearance: none; background: none; border: 0; padding: 2px 4px; font: inherit; display: inline-flex; gap: 8px;
  cursor: pointer; font-weight: 600; border-radius: var(--radius-sm, 6px); }
.ev-lvchg:focus-visible { outline: 2px solid var(--c-primary); outline-offset: 2px; }
.ev-lvup { color: var(--c-success); }
.ev-lvdown { color: var(--c-warning); }
.ev-tab.on { color: var(--c-primary); border-bottom-color: var(--c-primary); }
.ev-kind { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; cursor: pointer;
  border: 1.5px solid var(--c-border); border-radius: var(--radius-md); background: var(--c-surface-2); }
.ev-kind.on { border-color: var(--c-primary); background: var(--c-primary-bg, var(--c-surface-2)); }
.ev-kind span span { display: block; }
.ev-kind .t-cap { margin-top: 2px; }
.ev-tip { margin-top: 12px; padding: 8px 10px; border-radius: var(--radius-sm); background: var(--c-surface-2); }
.ev-tip .mdi { margin-right: 4px; }
/* เฟส 5 — คิวรอบันทึกการคืนเงิน + ปุ่มยกเลิกทั้งงาน */
.ev-refund-row { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--c-border); }
.ev-refund-row:last-child { border-bottom: 0; }
.ev-danger-btn { color: var(--c-error); }
.ev-sum { margin-top: 12px; display: flex; flex-direction: column; gap: 6px; font-size: 14px; }
.ev-radios { display: flex; gap: 16px; flex-wrap: wrap; padding: 4px 0; }
.ev-radio { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; cursor: pointer; }
@media (max-width: 560px) { .ev-refund-row { flex-wrap: wrap; } }

.ev-dim { color: var(--c-text-muted); font-size: 12px; }
.ev-chiprow { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0; min-height: 30px; }
.ev-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: var(--radius-pill); background: var(--c-primary); color: #fff; font-size: 13px; }
.ev-chip .mdi { cursor: pointer; }
.ev-picklist { max-height: 220px; overflow-y: auto; border: 1px solid var(--c-border, #eee); border-radius: 10px; }
.ev-pickrow { padding: 8px 12px; cursor: pointer; display: flex; align-items: center; gap: 8px; }
.ev-pickrow:hover { background: var(--c-surface-2, #faf5f7); }
.ev-review { border: 1px solid var(--c-border, #eee); border-radius: 10px; padding: 10px 12px; margin-bottom: 8px; }
.ev-review-head { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.ev-review-body { margin-top: 6px; display: flex; gap: 16px; flex-wrap: wrap; }
.ev-tag { font-size: 11px; padding: 2px 8px; border-radius: var(--radius-pill); background: var(--c-surface-2, #f2ecef); color: var(--c-text-muted); }
.ev-tag-warn { background: #fff3e0; color: #b26a00; }
.ev-tag-info { background: #e3f2fd; color: #1565c0; }
.ev-tag-paid { background: #e8f5e9; color: #2e7d32; }
.ev-radio { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; cursor: pointer; }
.ev-rosterrow { display: flex; justify-content: space-between; align-items: center; padding: 8px 4px; border-bottom: 1px solid var(--c-border, #f0eaee); gap: 10px; }
.ev-rostername { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ev-rosteract { display: flex; gap: 14px; font-size: 20px; flex: 0 0 auto; }
.ev-legacy-note { display: flex; align-items: center; gap: 8px; font-size: 13px; background: #fff3e0; color: #b26a00; padding: 8px 10px; border-radius: 8px; margin-bottom: 10px; }
.evreg-flag { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; }
.foot-left { margin-right: auto; }
</style>
